import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, TrendingUp, Calendar, Users, 
  ArrowUpRight, ArrowDownRight, Download, Filter,
  CreditCard, Wallet, PieChart, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '/api';

export default function EarningsAnalytics() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/instructor/earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarnings(response.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error(isEnglish ? 'Failed to load earnings' : 'Gelir verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate monthly data for chart
  const getMonthlyData = () => {
    if (!earnings?.monthly_breakdown) return [];
    
    const months = Object.entries(earnings.monthly_breakdown)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12); // Last 12 months
    
    return months.map(([month, amount]) => ({
      month: month,
      label: new Date(month + '-01').toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR', { month: 'short', year: '2-digit' }),
      amount: amount
    }));
  };

  const monthlyData = getMonthlyData();
  const maxAmount = Math.max(...monthlyData.map(d => d.amount), 1);

  // Filter transactions by period
  const filterTransactions = () => {
    if (!earnings?.transactions) return [];
    
    let filtered = [...earnings.transactions];
    const now = new Date();
    
    switch (selectedPeriod) {
      case 'week':
        const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(t => new Date(t.created_at) >= weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        filtered = filtered.filter(t => new Date(t.created_at) >= monthAgo);
        break;
      case 'year':
        const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        filtered = filtered.filter(t => new Date(t.created_at) >= yearAgo);
        break;
      default:
        break;
    }
    
    return filtered;
  };

  const filteredTransactions = filterTransactions();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-8 px-4 md:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">{isEnglish ? 'Your Earnings' : 'Kazancınız'}</p>
                <p className="text-3xl font-bold mt-1">
                  {formatCurrency(earnings?.total_earnings)}
                </p>
                <p className="text-green-100 text-xs mt-1">
                  %80 {isEnglish ? 'of total sales' : 'pay oranı'}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-lg">
                <Wallet className="h-8 w-8" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">{isEnglish ? 'Total Revenue' : 'Toplam Satış'}</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(earnings?.total_revenue)}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  {isEnglish ? 'Gross sales' : 'Brüt satış'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">{isEnglish ? 'Platform Fee' : 'Platform Payı'}</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency((earnings?.total_revenue || 0) - (earnings?.total_earnings || 0))}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  %{earnings?.platform_fee_percentage || 20} {isEnglish ? 'commission' : 'komisyon'}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <PieChart className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">{isEnglish ? 'Total Sales' : 'Satış Adedi'}</p>
                <p className="text-2xl font-bold mt-1">
                  {earnings?.total_sales || 0}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  {isEnglish ? 'Completed orders' : 'Tamamlanan sipariş'}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {isEnglish ? 'Monthly Earnings' : 'Aylık Kazanç'}
          </CardTitle>
          <CardDescription>
            {isEnglish ? 'Your earnings over the last 12 months' : 'Son 12 aydaki kazancınız'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {monthlyData.length > 0 ? (
            <div className="h-64">
              <div className="flex items-end justify-between h-48 gap-2">
                {monthlyData.map((data, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex justify-center">
                      <span className="text-xs text-gray-500">
                        {formatCurrency(data.amount)}
                      </span>
                    </div>
                    <div 
                      className="w-full bg-purple-500 rounded-t-md transition-all hover:bg-purple-600"
                      style={{ 
                        height: `${Math.max((data.amount / maxAmount) * 100, 5)}%`,
                        minHeight: '4px'
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 border-t pt-2">
                {monthlyData.map((data, index) => (
                  <div key={index} className="flex-1 text-center">
                    <span className="text-xs text-gray-500">{data.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              {isEnglish ? 'No earnings data yet' : 'Henüz kazanç verisi yok'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {isEnglish ? 'Transaction History' : 'İşlem Geçmişi'}
              </CardTitle>
              <CardDescription>
                {filteredTransactions.length} {isEnglish ? 'transactions' : 'işlem'}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">{isEnglish ? 'All Time' : 'Tüm Zamanlar'}</option>
                <option value="week">{isEnglish ? 'This Week' : 'Bu Hafta'}</option>
                <option value="month">{isEnglish ? 'This Month' : 'Bu Ay'}</option>
                <option value="year">{isEnglish ? 'This Year' : 'Bu Yıl'}</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length > 0 ? (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {filteredTransactions.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <ArrowUpRight className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {transaction.lesson_title || transaction.lesson?.title || 'Kurs Satışı'}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{transaction.buyer_name || 'Müşteri'}</span>
                        <span>•</span>
                        <span>{formatDate(transaction.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">
                      +{formatCurrency(transaction.instructor_amount)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isEnglish ? 'Total:' : 'Toplam:'} {formatCurrency(transaction.total_amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{isEnglish ? 'No transactions yet' : 'Henüz işlem yok'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Info */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Wallet className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">
                {isEnglish ? 'Payout Information' : 'Ödeme Bilgileri'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {isEnglish 
                  ? 'Payouts are processed monthly. Your earnings will be transferred to your registered IBAN on the 1st of each month.'
                  : 'Ödemeler aylık olarak işlenir. Kazancınız her ayın 1\'inde kayıtlı IBAN\'ınıza aktarılır.'}
              </p>
              <div className="mt-3 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  <span>{isEnglish ? 'Next payout:' : 'Sonraki ödeme:'}</span>
                  <span className="font-medium">
                    {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
